import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
  Grid,
  Card,
  Divider,
  Icon,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
  Button,
  Stack,
} from "@mui/material";
import PrintIcon from "@mui/icons-material/Print";
import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { pageHeaderStyles } from "utils/pageHeaderStyles";
import { buttonStyles } from "utils/buttonStyles";
import { tabStyles } from "utils/tabStyles";
import { tableStyles } from "utils/tableStyles";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { useMaterialUIController } from "context";
import { useSnackbar } from "components/GlobalSnackbar";
import { useConfirmDialog } from "components/ConfirmDialog";
import { handleApiError } from "utils/errorHandler";

// API imports
import { apiGetHartaByKodHarta } from "api/harta";
import { apiGetLejarByKodHartaViaAkaun, apiDeleteLejarCascade, apiGetLejarForPrint } from "api/lejar";
import { apiGetDepositByNoAkaun, apiCreateDeposit, apiDeleteDeposit } from "api/deposit";
import { apiGetInboisByNoAkaun, apiHapusInbois } from "api/inbois";
// Use cross-department Terimaan fetch to ensure ALL receipts for the account are shown
import { getTerimaanByNoAkaunAll } from "api/terimaan";
import TambahDeposit from "components/STPopup/TambahDeposit";
import { printInboisPreview } from "components/STPrint/PrintInbois";

// Utils
import { getCurrentUserDepartment } from "utils/userUtils";

function HartaDetails() {
  const { kodHarta, noAkaun } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [controller] = useMaterialUIController();
  const { darkMode, user } = controller;
  const { showSuccess, showError, showInfo, showWarning } = useSnackbar();
  const { showConfirm } = useConfirmDialog();
  
  // Get customer context passed from CustomerProfile (if any)
  const customerContext = location.state || {};
  
  // Helper function to get no_akaun consistently
  const getNoAkaun = () => accountData?.no_akaun || noAkaun || null;

  // State for data
  const [accountData, setAccountData] = useState(noAkaun ? { noakaun: noAkaun } : null);
  const [hartaData, setHartaData] = useState(null);
  const [lejarData, setLejarData] = useState([]);
  const [depositData, setDepositData] = useState([]);
  const [inboisData, setInboisData] = useState([]); // Add inbois data state
  const [selectedLejarIds, setSelectedLejarIds] = useState([]);
  const [selectedDepositIds, setSelectedDepositIds] = useState([]);
  const [selectedInboisIds, setSelectedInboisIds] = useState([]);
  const [terimaanData, setTerimaanData] = useState([]);
  const [selectedTerimaanIds, setSelectedTerimaanIds] = useState([]);
  // Active tab state must be declared before any hooks/effects that reference it
  const [activeTab, setActiveTab] = useState(0);
  // --- Sorting helpers to keep stable ordering across tabs ---
  const normalizePerkara = (s) => (s || "").toString().toLowerCase().trim().replace(/\s+/g, " ");
  const parseDateSafe = (d) => (d ? new Date(d) : null);
  const isZero = (v) => Number(v || 0) === 0;
  const isBakiAwalTunggakan = (r) => normalizePerkara(r?.perkara) === "baki awal tunggakan" && isZero(r?.debit) && isZero(r?.kredit);
  const lejarComparator = (a, b) => {
    // Pin special rows first
    const aPin = isBakiAwalTunggakan(a) ? 0 : 1;
    const bPin = isBakiAwalTunggakan(b) ? 0 : 1;
    if (aPin !== bPin) return aPin - bPin;
    // tarikh_resit ASC, NULLS LAST
    const aDate = parseDateSafe(a?.tarikh_resit) || null;
    const bDate = parseDateSafe(b?.tarikh_resit) || null;
    if (aDate && bDate) {
      const diff = aDate - bDate;
      if (diff !== 0) return diff;
    } else if (aDate && !bDate) {
      return -1;
    } else if (!aDate && bDate) {
      return 1;
    }
    // If same date AND same doc_no (fallback no_resit), sort by Perkara ASC
    const aDoc = (a?.doc_no ?? a?.no_resit ?? "").toString().trim();
    const bDoc = (b?.doc_no ?? b?.no_resit ?? "").toString().trim();
    if (aDoc && bDoc && aDoc === bDoc) {
      const cmpPer = normalizePerkara(a?.perkara).localeCompare(normalizePerkara(b?.perkara), "ms", { sensitivity: "base", numeric: true });
      if (cmpPer !== 0) return cmpPer;
    }
    // id ASC fallback
    return (Number(a?.id || 0) - Number(b?.id || 0));
  };
  const terimaanComparator = (a, b) => {
    const aDate = parseDateSafe(a?.tarikh_resit) || null;
    const bDate = parseDateSafe(b?.tarikh_resit) || null;
    if (aDate && bDate) {
      const diff = aDate - bDate;
      if (diff !== 0) return diff;
    } else if (aDate && !bDate) {
      return -1;
    } else if (!aDate && bDate) {
      return 1;
    }
    const aResit = (a?.no_resit ?? "").toString().trim();
    const bResit = (b?.no_resit ?? "").toString().trim();
    if (aResit && bResit && aResit === bResit) {
      const cmpPer = normalizePerkara(a?.perkara).localeCompare(normalizePerkara(b?.perkara), "ms", { sensitivity: "base", numeric: true });
      if (cmpPer !== 0) return cmpPer;
    }
    return (Number(a?.id || 0) - Number(b?.id || 0));
  };
  const inboisComparator = (a, b) => {
    const aDate = parseDateSafe(a?.tarikh_inbois) || null;
    const bDate = parseDateSafe(b?.tarikh_inbois) || null;
    if (aDate && bDate) {
      const diff = aDate - bDate;
      if (diff !== 0) return diff;
    } else if (aDate && !bDate) {
      return -1;
    } else if (!aDate && bDate) {
      return 1;
    }
    const aNo = (a?.no_inbois ?? "").toString().trim();
    const bNo = (b?.no_inbois ?? "").toString().trim();
    if (aNo && bNo && aNo === bNo) {
      const cmpPer = normalizePerkara(a?.perkara).localeCompare(normalizePerkara(b?.perkara), "ms", { sensitivity: "base", numeric: true });
      if (cmpPer !== 0) return cmpPer;
    }
    return (Number(a?.id || 0) - Number(b?.id || 0));
  };
  // Refresh data when switching to specific tabs (Lejar=1, Deposit=2, Inbois=3, Terimaan=4)
  useEffect(() => {
    const noAkaunToUse = accountData?.noakaun || accountData?.no_akaun || null;
    if (!noAkaunToUse) return;
    const fetchForTabSwitch = async () => {
      try {
        if (activeTab === 1) {
          const lejarApi = await import('api/lejar');
          const res = await lejarApi.apiGetLejarByNoAkaun(noAkaunToUse);
          setLejarData((res?.data || []).slice().sort(lejarComparator));
        } else if (activeTab === 2) {
          const depositRes = await apiGetDepositByNoAkaun(noAkaunToUse);
          setDepositData((depositRes?.data || []).slice().sort((a, b) => new Date(a.tarikh_resit) - new Date(b.tarikh_resit)));
        } else if (activeTab === 3) {
          const inboisRes = await apiGetInboisByNoAkaun(noAkaunToUse);
          setInboisData((inboisRes?.data || []).slice().sort(inboisComparator));
        } else if (activeTab === 4) {
          const terimaanApi = await import('api/terimaan');
          // Fetch ALL terimaan across departments for this account
          const res = await terimaanApi.getTerimaanByNoAkaunAll(noAkaunToUse);
          setTerimaanData((res?.data || []).slice().sort(terimaanComparator));
        }
      } catch (err) {
        console.error('Tab refresh failed:', err);
      }
    };
    fetchForTabSwitch();
  }, [activeTab]);
  const [isLejarLoading, setIsLejarLoading] = useState(false);
  const [isDepositLoading, setIsDepositLoading] = useState(false);
  const [isInboisLoading, setIsInboisLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userDepartment, setUserDepartment] = useState("");
  const [authLoading, setAuthLoading] = useState(true);
  const [tambahDepositOpen, setTambahDepositOpen] = useState(false);
  const [depositJustSaved, setDepositJustSaved] = useState(false);

  // Check authentication state - optimized to be faster
  useEffect(() => {
    const checkAuth = () => {
      if (user !== undefined) {
        setAuthLoading(false);
      }
    };

    checkAuth();
    // Reduced timeout for faster initial load
    const timeout = setTimeout(() => {
      setAuthLoading(false);
    }, 200); // Changed from 1000ms to 200ms

    return () => clearTimeout(timeout);
  }, [user]);

  useEffect(() => {
    // Set user department when component mounts
    setUserDepartment(getCurrentUserDepartment());
  }, []);

  useEffect(() => {
    const fetchHartaData = async () => {
      if (authLoading || !user) return;
      try {
        setLoading(true);
        setError(null);

        // Fetch harta data by kod harta
        const response = await apiGetHartaByKodHarta(kodHarta);
        if (response && response.data && response.data.length > 0) {
          setHartaData(response.data[0]);

          // Fetch account data - ESSENTIAL for initial load
          try {
            const accountsResponse = await fetch(
              `/api/akaun/kodharta/${encodeURIComponent(kodHarta)}`,
              {
                method: "GET",
                headers: {
                  "Content-Type": "application/json",
                  "X-User-Department": getCurrentUserDepartment(),
                },
              }
            );
            if (accountsResponse.ok) {
              const result = await accountsResponse.json();
              const accounts = result.data || result;
              let selectedAccount = null;
              if (accounts && accounts.length > 0) {
                // DB column name is no_akaun
                selectedAccount = accounts.find((acc) => acc.no_akaun === noAkaun);
                if (!selectedAccount) selectedAccount = accounts[0];
                setAccountData(selectedAccount);
              } else {
                setAccountData(null);
              }
            } else {
              setAccountData(null);
            }
          } catch (accountError) {
            console.error("Error fetching account data:", accountError);
            setAccountData(null);
          }

          // NOTE: Lejar, Deposit, Inbois, Terimaan data will be loaded when user switches to those tabs
          // This significantly improves initial page load time
        } else {
          setError("Harta tidak dijumpai");
        }
      } catch (err) {
        console.error("Error fetching harta data:", err);
        setError("Gagal memuatkan data harta");
      } finally {
        setLoading(false);
      }
    };
    if (kodHarta && !authLoading) {
      fetchHartaData();
    }
  }, [kodHarta, authLoading, user]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Refresh current tab's data whenever the tab changes
  useEffect(() => {
    // Only refresh data for tabs with tables (1..4)
    if (activeTab === 0) return;
    const noAkaunToUse = getNoAkaun();
    if (!noAkaunToUse) return;

    let cancelled = false;

    const fetchForTab = async () => {
      try {
        switch (activeTab) {
          case 1: { // Lejar
            setIsLejarLoading(true);
            // Removed silent auto-rebuild - users can use the explicit "Rebuild Lejar" button if needed
            const lejarApi = await import("api/lejar");
            const res = await lejarApi.apiGetLejarByNoAkaun(noAkaunToUse);
            if (!cancelled) setLejarData((res?.data || []).slice().sort(lejarComparator));
            break;
          }
          case 2: { // Deposit
            setIsDepositLoading(true);
            const res = await apiGetDepositByNoAkaun(noAkaunToUse);
            let data = res?.data || [];
            // Sort by tarikh_resit ASC
            data = [...data].sort((a, b) => new Date(a.tarikh_resit) - new Date(b.tarikh_resit));
            if (!cancelled) setDepositData(data);
            break;
          }
          case 3: { // Inbois
            setIsInboisLoading(true);
            const res = await apiGetInboisByNoAkaun(noAkaunToUse);
            let data = res?.data || [];
            data = [...data].sort(inboisComparator);
            if (!cancelled) setInboisData(data);
            break;
          }
          case 4: { // Terimaan (cross-department)
            const res = await getTerimaanByNoAkaunAll(noAkaunToUse);
            let data = res?.data || [];
            data = [...data].sort(terimaanComparator);
            if (!cancelled) setTerimaanData(data);
            break;
          }
          default:
            break;
        }
      } catch (err) {
        console.error("Tab refresh failed:", err);
        // Non-blocking: keep previous data on error
      } finally {
        if (!cancelled) {
          if (activeTab === 1) setIsLejarLoading(false);
          if (activeTab === 2) setIsDepositLoading(false);
          if (activeTab === 3) setIsInboisLoading(false);
          // Terimaan has no dedicated loading flag
        }
      }
    };

    fetchForTab();
    return () => {
      cancelled = true;
    };
  }, [activeTab, accountData]);

  // Lejar checkbox handlers
  const handleSelectAllLejar = (event) => {
    if (event.target.checked) {
      const allIds = lejarData.map((item) => item.id);
      setSelectedLejarIds(allIds);
    } else {
      setSelectedLejarIds([]);
    }
  };

  const handleSelectLejar = (id) => {
    setSelectedLejarIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  // Lejar print handler
  const handlePrintLejar = async () => {
    try {
      setIsLejarLoading(true);
      // Get no akaun using helper function
      const noAkaunToUse = getNoAkaun();
      if (!noAkaunToUse) {
  window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'No Akaun tidak dijumpai untuk cetakan lejar.' });
        return;
      }
      // Dynamically import the correct API (to match the rest of the code)
      const lejarApi = await import("api/lejar");
      const response = await lejarApi.apiGetLejarByNoAkaun(noAkaunToUse);
      if (response.data) {
        const sortedLejar = [...response.data].sort(lejarComparator);
        // Create print window
        const printWindow = window.open("", "_blank");
        if (printWindow) {
          const currentUser = JSON.parse(localStorage.getItem("user") || "{}");
          // Get account info from the first lejar record or hartaData
          const firstRecord = sortedLejar[0];
          const noAkaun = firstRecord?.no_akaun || hartaData?.no_akaun || "-";
          const nama = firstRecord?.nama || hartaData?.nama || "-";
          // Format alamat from harta data
          const alamatParts = [
            hartaData?.alamat1_harta,
            hartaData?.alamat2_harta,
            hartaData?.poskod_harta,
            hartaData?.bandar_harta,
            hartaData?.negeri_harta,
          ].filter(Boolean);
          const alamat = alamatParts.join(", ") || "-";
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>Laporan Lejar</title>
                 <style>
                   body { 
                     font-family: Arial, sans-serif; 
                     margin: 20px; 
                     font-size: 12px; 
                   }
                   .header { 
                     text-align: center; 
                     margin-bottom: 20px; 
                   }
                   .title { 
                     font-weight: bold; 
                     font-size: 14px; 
                     margin-bottom: 10px; 
                     text-align: left; /* left-align the title only */
                   }
                  .account-info {
                    text-align: left;
                    margin-bottom: 15px;
                    font-size: 10px;
                  }
                  table { 
                    width: 100%; 
                    border-collapse: collapse; 
                    border: 1px solid black; 
                  }
                  th, td { 
                    border: 1px solid black; 
                    padding: 4px; 
                    text-align: left; 
                    font-size: 10px; 
                  }
                  th { 
                    background-color: #f0f0f0; 
                    font-weight: bold; 
                    text-align: center; 
                  }
                  .number { 
                    text-align: center; 
                  }
                  .amount { 
                    text-align: right; 
                  }
                  .word { 
                    text-align: center; 
                  }
                  .footer { 
                    margin-top: 20px; 
                    display: flex; 
                    justify-content: space-between; 
                    font-size: 10px; 
                  }
                   @media print {
                     @page { size: A4; margin: 12mm; }
                     body { margin: 10px; }
                   }
                </style>
              </head>
              <body>
                <div class="header">
                  <div class="title">
                    LAPORAN LEJAR ${currentUser.bahagian ? currentUser.bahagian.toUpperCase() : ""}
                  </div>
                  <div class="account-info">
                    <div><strong>No Akaun:</strong> ${noAkaun}</div>
                    <div><strong>Nama:</strong> ${nama}</div>
                    <div><strong>Alamat:</strong> ${
                      (hartaData?.kategori_kutipan || "-") + " / " + alamat
                    }</div>
                  </div>
                </div>
                <table>
                  <thead>
                    <tr>
                      <th style="width: 30px;">BIL</th>
                      <th style="width: 80px;">NO. AKAUN</th>
                      <th style="width: 80px;">TARIKH</th>
                      <th style="width: 80px;">DOC. NO</th>
                      <th style="width: 200px;">PERKARA</th>
                      <th style="width: 80px;">DEBIT (RM)</th>
                      <th style="width: 80px;">KREDIT (RM)</th>
                      <th style="width: 80px;">BAKI (RM)</th>
                    </tr>
                  </thead>
                  <tbody>
                    ${sortedLejar
                      .map(
                        (row, index) => `
                      <tr>
                        <td class="word">${index + 1}</td>
                        <td class="word">${row.no_akaun || "-"}</td>
                        <td class="word">${
                          row.tarikh_resit
                            ? new Date(row.tarikh_resit).toLocaleDateString("en-CA")
                            : "-"
                        }</td>
                        <td class="word">${row.doc_no || row.no_resit || "-"}</td>
                        <td>${row.perkara || "-"}</td>
                        <td class="amount">${Number(row.debit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td class="amount">${Number(row.kredit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                        <td class="amount">${Number(row.baki || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      </tr>
                    `
                      )
                      .join("")}
                  </tbody>
                </table>
                <div class="footer">
                  <div>Jumlah rekod: ${sortedLejar.length}</div>
                  <div>Dicetak pada: ${new Date().toLocaleString("ms-MY")}</div>
                </div>
              </body>
            </html>
          `);
          printWindow.document.close();
          setTimeout(() => {
            printWindow.print();
          }, 500);
        }
      }
    } catch (error) {
      console.error("Error printing lejar:", error);
  window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Gagal mencetak laporan lejar. Sila cuba lagi.' });
    } finally {
      setIsLejarLoading(false);
    }
  };

  // Lejar delete handler
  const handleDeleteSelectedLejar = async () => {
    if (selectedLejarIds.length === 0) {
      showInfo('Sila pilih rekod lejar untuk dihapus');
      return;
    }

    const confirmed = await showConfirm({
      title: `Padam ${selectedLejarIds.length} Rekod Lejar`,
      message: `Adakah anda pasti untuk memadam ${selectedLejarIds.length} rekod lejar? Tindakan ini akan memadam inbois dan inbois detail yang berkaitan.`,
      confirmText: 'Padam',
      type: 'error'
    });
    
    if (confirmed) {
      try {
        setIsLejarLoading(true);
        // Always cascade delete related Inbois and Inbois Detail to keep data tally
        for (const id of selectedLejarIds) {
          await apiDeleteLejarCascade(id);
        }

        // Refresh lejar data for the current account (more precise than by kod_harta)
        const noAkaunToUse = getNoAkaun();
        if (noAkaunToUse) {
          try {
            const lejarApi = await import("api/lejar");
            const lejarResponse = await lejarApi.apiGetLejarByNoAkaun(noAkaunToUse);
            setLejarData((lejarResponse?.data || []).slice().sort(lejarComparator));
            // Also refresh Inbois list to reflect cascaded deletions
            try {
              const inboisResponse = await apiGetInboisByNoAkaun(noAkaunToUse);
              setInboisData(inboisResponse?.data || []);
            } catch (_) {
              // ignore
            }
          } catch (e) {
            // If no data (404), just clear the table
            setLejarData([]);
          }
        } else {
          setLejarData([]);
        }
        setSelectedLejarIds([]);
        showSuccess(`${selectedLejarIds.length} rekod lejar telah berjaya dihapus`);
      } catch (error) {
        console.error("Error deleting lejar records:", error);
        handleApiError(error, showError);
      } finally {
        setIsLejarLoading(false);
      }
    }
  };

  // Deposit handlers
  const handleSelectAllDeposit = (event) => {
    if (event.target.checked) {
      setSelectedDepositIds(depositData.map((deposit) => deposit.id));
    } else {
      setSelectedDepositIds([]);
    }
  };

  const handleSelectDeposit = (id) => {
    setSelectedDepositIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleTambahDeposit = () => {
    setTambahDepositOpen(true);
  };

  const handleSaveDeposit = async (depositData) => {
    try {
      setIsDepositLoading(true);
      await apiCreateDeposit(depositData);
      // Mark that we just saved a deposit, so we can refresh again after popup closes
      setDepositJustSaved(true);
      // Refresh deposit data immediately using the current account (mirror deletion logic)
      const noAkaunToUse = depositData?.no_akaun || getNoAkaun();
      if (noAkaunToUse) {
        const depositResponse = await apiGetDepositByNoAkaun(noAkaunToUse);
        setDepositData(depositResponse?.data || []);
      }
    } catch (error) {
      console.error("Error creating deposit:", error);
      throw error; // Re-throw to be handled by popup
    } finally {
      setIsDepositLoading(false);
    }
  };

  // When popup closes, also refresh deposits if we just saved
  const handleCloseTambahDeposit = async () => {
    setTambahDepositOpen(false);
    if (depositJustSaved) {
      try {
        const noAkaunToUse = getNoAkaun();
        if (noAkaunToUse) {
          const depositResponse = await apiGetDepositByNoAkaun(noAkaunToUse);
          setDepositData(depositResponse?.data || []);
        }
      } finally {
        setDepositJustSaved(false);
      }
    }
  };

  const handleDeleteSelectedDeposit = async () => {
    if (selectedDepositIds.length === 0) {
      showInfo('Sila pilih rekod deposit untuk dihapus');
      return;
    }
    
    const confirmed = await showConfirm({
      title: `Padam ${selectedDepositIds.length} Rekod Deposit`,
      message: `Adakah anda pasti untuk memadam ${selectedDepositIds.length} rekod deposit?`,
      confirmText: 'Padam',
      type: 'error'
    });
    
    if (confirmed) {
      try {
        setIsDepositLoading(true);
        for (const id of selectedDepositIds) {
          await apiDeleteDeposit(id);
        }
        const noAkaunToUse = getNoAkaun();
        if (noAkaunToUse) {
          const depositResponse = await apiGetDepositByNoAkaun(noAkaunToUse);
          setDepositData(depositResponse?.data || []);
        } else {
          setDepositData([]);
        }
        setSelectedDepositIds([]);
        showSuccess('Rekod deposit berjaya dihapus');
      } catch (error) {
        console.error("Error deleting deposit records:", error);
        handleApiError(error, showError);
      } finally {
        setIsDepositLoading(false);
      }
    }
  };

  // Inbois selection handlers
  // Terimaan selection handlers
  const handleSelectAllTerimaan = (event) => {
    if (event.target.checked) {
      setSelectedTerimaanIds(terimaanData.map(t => t.id));
    } else {
      setSelectedTerimaanIds([]);
    }
  };
  const handleSelectTerimaan = (id) => {
    setSelectedTerimaanIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };
  const handleDeleteSelectedTerimaan = async () => {
    if (selectedTerimaanIds.length === 0) {
      showInfo('Sila pilih rekod terimaan untuk dihapus');
      return;
    }
    
    const confirmed = await showConfirm({
      title: `Padam ${selectedTerimaanIds.length} Rekod Terimaan`,
      message: `Adakah anda pasti untuk memadam ${selectedTerimaanIds.length} rekod terimaan?`,
      confirmText: 'Padam',
      type: 'error'
    });
    
    if (confirmed) {
      try {
        const terimaanApi = await import('api/terimaan');
        setIsInboisLoading(true);
        await terimaanApi.apiDeleteTerimaan(selectedTerimaanIds);
        const noAkaunToUse = accountData?.noakaun || accountData?.no_akaun || null;
        if (noAkaunToUse) {
          try {
            const [terimaanRes, lejarModule] = await Promise.all([
              terimaanApi.getTerimaanByNoAkaunAll(noAkaunToUse),
              import('api/lejar')
            ]);
            const sortedTerimaan = (terimaanRes?.data || []).slice().sort((a,b)=> new Date(a.tarikh_resit) - new Date(b.tarikh_resit));
            setTerimaanData(sortedTerimaan);
            const lejarRes = await lejarModule.apiGetLejarByNoAkaun(noAkaunToUse);
            setLejarData((lejarRes?.data || []).slice().sort(lejarComparator));
          } catch (refreshErr) {
            console.error('Failed refreshing after terimaan delete:', refreshErr);
          }
        }
        setSelectedTerimaanIds([]);
        window.appNotify && window.appNotify({ color: 'success', icon: 'check_circle', title: 'Berjaya', content: 'Terimaan dan lejar berkaitan berjaya dihapus.' });
      } catch (err) {
        console.error('Error deleting terimaan:', err);
        window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Gagal memadam rekod terimaan.' });
      } finally {
        setIsInboisLoading(false);
      }
    }
  };
  const handleSelectInbois = (id) => {
    setSelectedInboisIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((selectedId) => selectedId !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const handleSelectAllInbois = (event) => {
    if (event.target.checked) {
      setSelectedInboisIds(inboisData.map((inbois) => inbois.id));
    } else {
      setSelectedInboisIds([]);
    }
  };

  const handleDeleteSelectedInbois = async () => {
    if (selectedInboisIds.length === 0) {
      showInfo('Sila pilih rekod inbois untuk dihapus');
      return;
    }
    
    const confirmed = await showConfirm({
      title: `Padam ${selectedInboisIds.length} Rekod Inbois`,
      message: `Adakah anda pasti untuk memadam ${selectedInboisIds.length} rekod inbois?`,
      confirmText: 'Padam',
      type: 'danger'
    });
    
    if (confirmed) {
      try {
        const inboisApi = await import('api/inbois');
        setIsInboisLoading(true);
        setIsLejarLoading(true);
        await inboisApi.apiHapusInbois(selectedInboisIds);
        
        // Refresh both inbois and lejar data
        const noAkaunToUse = getNoAkaun();
        if (noAkaunToUse) {
          try {
            const inboisRes = await inboisApi.apiGetInboisByNoAkaun(noAkaunToUse);
            setInboisData(inboisRes?.data || []);
          } catch (e) {
            setInboisData([]);
          }
          
          try {
            const lejarApi = await import("api/lejar");
            const lejarRes = await lejarApi.apiGetLejarByNoAkaun(noAkaunToUse);
            setLejarData((lejarRes?.data || []).slice().sort(lejarComparator));
          } catch (e) {
            setLejarData([]);
          }
        } else {
          setInboisData([]);
          setLejarData([]);
        }
        setSelectedInboisIds([]);
        showSuccess('Inbois dan lejar berkaitan berjaya dihapus');
      } catch (error) {
        console.error('Error deleting inbois:', error);
        handleApiError(error, showError);
      } finally {
        setIsInboisLoading(false);
        setIsLejarLoading(false);
      }
    }
  };

  // Inbois print handler
  const handlePrintInbois = async () => {
    try {
      if (!inboisData || inboisData.length === 0) {
  window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Tiada rekod inbois untuk dicetak.' });
        return;
      }
      if (selectedInboisIds.length !== 1) {
  window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Sila pilih satu (1) inbois untuk cetak.' });
        return;
      }
      const selected = inboisData.find((r) => r.id === selectedInboisIds[0]);
      if (!selected) {
  window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'Rekod inbois tidak dijumpai.' });
        return;
      }
      await printInboisPreview(selected);
    } catch (err) {
      console.error("Error printing inbois:", err);
  window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Gagal mencetak inbois. Sila cuba lagi.' });
    }
  };

  // Handler for Rebuild Lejar
  const handleRebuildLejar = async () => {
    try {
      setIsLejarLoading(true);
      const noAkaunToUse = getNoAkaun();
      if (!noAkaunToUse) {
  window.appNotify && window.appNotify({ color: 'warning', icon: 'warning', title: 'Makluman', content: 'No Akaun tidak dijumpai untuk rebuild lejar.' });
        return;
      }
      // Call backend to recalculate lejar baki and update DB (with CSRF headers)
      const { fetchWithCsrfRetry } = await import("api/csrf");
      const response = await fetchWithCsrfRetry(`/api/lejar/rebuild/${encodeURIComponent(noAkaunToUse)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      if (!response.ok) {
        throw new Error("Gagal rebuild lejar di server.");
      }
  // After backend updates, fetch latest lejar data
      const lejarApi = await import("api/lejar");
    const lejarResponse = await lejarApi.apiGetLejarByNoAkaun(noAkaunToUse);
  setLejarData((lejarResponse?.data || []).slice().sort(lejarComparator));
  window.appNotify && window.appNotify({ color: 'success', icon: 'check_circle', title: 'Berjaya', content: 'Lejar berjaya dikira semula dan dikemaskini.' });
    } catch (err) {
      console.error("Error rebuilding lejar:", err);
  window.appNotify && window.appNotify({ color: 'error', icon: 'error', title: 'Ralat', content: 'Gagal rebuild lejar. Sila cuba lagi.' });
    } finally {
      setIsLejarLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 0: // Maklumat Harta
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={2} px={2}>
                  <MDTypography variant="h6" fontWeight="medium" textTransform="capitalize">
                    Maklumat Harta
                  </MDTypography>
                </MDBox>
                <MDBox p={2}>
                  <MDBox opacity={0.3}>
                    <Divider />
                  </MDBox>
                  {hartaData && (
                    <MDBox pt={0} pb={1} px={2}>
                      <MDBox component="ul" display="flex" flexDirection="column" p={0} m={0}>
                        <MDBox
                          component="li"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          py={0.5}
                          pr={1}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                            sx={{ minWidth: "150px" }}
                          >
                            Kod Harta:
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            sx={{ textAlign: "right", flex: 1 }}
                          >
                            {hartaData.kod_harta || "N/A"}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="li"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          py={0.5}
                          pr={1}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                            sx={{ minWidth: "150px" }}
                          >
                            Alamat Harta:
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            sx={{ textAlign: "right", flex: 1 }}
                          >
                            {[
                              hartaData.alamat1_harta,
                              hartaData.alamat2_harta,
                              hartaData.poskod_harta,
                              hartaData.bandar_harta,
                              hartaData.negeri_harta,
                            ]
                              .filter(Boolean)
                              .join(", ") || "N/A"}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="li"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          py={0.5}
                          pr={1}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                            sx={{ minWidth: "150px" }}
                          >
                            Zon:
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            sx={{ textAlign: "right", flex: 1 }}
                          >
                            {hartaData.zon || "N/A"}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="li"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          py={0.5}
                          pr={1}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                            sx={{ minWidth: "150px" }}
                          >
                            Kadar Sewa:
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            sx={{ textAlign: "right", flex: 1 }}
                          >
                            RM {hartaData.kadar_sewa || "0.00"}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="li"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          py={0.5}
                          pr={1}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                            sx={{ minWidth: "150px" }}
                          >
                            Status:
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            sx={{ textAlign: "right", flex: 1 }}
                          >
                            {hartaData.status || "N/A"}
                          </MDTypography>
                        </MDBox>
                        <MDBox
                          component="li"
                          display="flex"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          py={0.5}
                          pr={1}
                        >
                          <MDTypography
                            variant="button"
                            fontWeight="bold"
                            color="text"
                            sx={{ minWidth: "150px" }}
                          >
                            Kategori Kutipan:
                          </MDTypography>
                          <MDTypography
                            variant="button"
                            fontWeight="regular"
                            color="text"
                            sx={{ textAlign: "right", flex: 1 }}
                          >
                            {hartaData.kategori_kutipan || "N/A"}
                          </MDTypography>
                        </MDBox>
                      </MDBox>
                    </MDBox>
                  )}
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 1: // Lejar
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={2} px={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Lejar
                  </MDTypography>
                </MDBox>
                <MDBox p={2}>
                  <MDBox pt={1} pb={2} px={2}>
                    {/* Action buttons */}
                    <MDBox sx={{ pb: 2 }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid
                          item
                          xs={6}
                          md={6}
                          lg={6}
                          sx={{ display: "flex", justifyContent: "flex-start", gap: 1, pt: 1 }}
                        >
                          <Button
                            variant="contained"
                            startIcon={<PrintIcon />}
                            size="small"
                            onClick={handlePrintLejar}
                            disabled={isLejarLoading}
                            sx={buttonStyles.primary}
                          >
                            Cetak Lejar
                          </Button>
                          <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<RefreshIcon />}
                            onClick={handleRebuildLejar}
                            disabled={isLejarLoading}
                            sx={buttonStyles.primary}
                          >
                            Rebuild Lejar
                          </Button>
                        </Grid>
                        <Grid
                          item
                          xs={6}
                          md={6}
                          lg={6}
                          sx={{ display: "flex", justifyContent: "flex-end", gap: 1, pt: 1 }}
                        >
                          {/* HAPUS TERPILIH button removed - users should delete from Inbois tab instead */}
                        </Grid>
                      </Grid>
                    </MDBox>

                    {lejarData.length > 0 ? (
                      <MDBox>
                        <Grid>
                          <MDTypography variant="caption" fontWeight="bold" sx={{ mr: 1 }}>
                            Rekod lejar untuk Kod Harta:{" "}
                          </MDTypography>
                          <MDTypography variant="body2" fontWeight="bold" color="#f42020ff">
                            {hartaData?.kod_harta}
                          </MDTypography>
                        </Grid>
                        <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
                          <Table size="small">
                            <TableBody>
                              {/* Header row */}
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="caption" fontWeight="bold">
                                    {/* Empty for row number */}
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    No Akaun
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Tarikh
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Doc. No
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Perkara
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Debit
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Kredit
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Baki
                                  </MDTypography>
                                </TableCell>
                                {/* Removed Baki column per requirement */}
                              </TableRow>
                              {/* Data rows */}
                              {(() => {
                                // Render in the order provided by backend (already canonicalized)
                                return lejarData.map((lejar, index) => (
                                  <TableRow key={index} hover>
                                    <TableCell>
                                      <MDTypography variant="caption" fontWeight="regular">
                                        {index + 1}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <MDTypography variant="caption" fontWeight="regular">
                                        {lejar.no_akaun || "N/A"}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <MDTypography variant="caption" fontWeight="regular">
                                        {lejar.tarikh_resit
                                          ? new Date(lejar.tarikh_resit).toLocaleDateString("en-CA")
                                          : "N/A"}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="center">
                                      <MDTypography variant="caption" fontWeight="regular">
                                        {lejar.doc_no || lejar.no_inbois || "N/A"}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell>
                                      <MDTypography
                                        variant="caption"
                                        fontWeight={typeof lejar.perkara === 'string' && lejar.perkara.startsWith('INV:') ? 'bold' : 'regular'}
                                        sx={typeof lejar.perkara === 'string' && lejar.perkara.startsWith('INV:') ? { color: '#1565c0' } : undefined}
                                        title={typeof lejar.perkara === 'string' && lejar.perkara.startsWith('INV:') ? 'Rekod Inbois' : undefined}
                                      >
                                        {lejar.perkara || "N/A"}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="caption" fontWeight="regular">
                                        {Number(lejar.debit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="caption" fontWeight="regular">
                                        {Number(lejar.kredit || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </MDTypography>
                                    </TableCell>
                                    <TableCell align="right">
                                      <MDTypography variant="caption" fontWeight="regular">
                                        {Number(lejar.baki || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                      </MDTypography>
                                    </TableCell>
                                  </TableRow>
                                ));
                              })()}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    ) : (
                      <MDTypography variant="body2" color="text">
                        Tiada rekod lejar dijumpai untuk harta ini.
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 2: // Deposit
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={2} px={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Deposit
                  </MDTypography>
                </MDBox>
                <MDBox p={2}>
                  <MDBox pt={1} pb={2} px={2}>
                    {/* Action buttons */}
                    <Stack direction="row" spacing={2} mb={2}>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={handleTambahDeposit}
                        disabled={isDepositLoading}
                        sx={buttonStyles.save}
                      >
                        Tambah Deposit
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<DeleteIcon />}
                        size="small"
                        sx={buttonStyles.delete}
                        onClick={handleDeleteSelectedDeposit}
                        disabled={selectedDepositIds.length === 0 || isDepositLoading}
                      >
                        Hapus Deposit ({selectedDepositIds.length})
                      </Button>
                    </Stack>

                    {depositData.length > 0 ? (
                      <MDBox>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ mr: 1 }}>
                          Rekod deposit untuk Kod Harta:{" "}
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold" color="#f42020ff">
                          {hartaData?.kod_harta}
                        </MDTypography>
                        <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
                          <Table size="small">
                            <TableBody>
                              {/* Header row */}
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="caption" fontWeight="bold"></MDTypography>
                                </TableCell>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    indeterminate={
                                      selectedDepositIds.length > 0 &&
                                      selectedDepositIds.length < depositData.length
                                    }
                                    checked={
                                      depositData.length > 0 &&
                                      selectedDepositIds.length === depositData.length
                                    }
                                    onChange={handleSelectAllDeposit}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    No Akaun
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Tarikh
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Doc. No
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Perkara
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Jumlah
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Baki
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              {/* Data rows */}
                              {depositData.map((deposit, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {index + 1}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={selectedDepositIds.includes(deposit.id)}
                                      onChange={() => handleSelectDeposit(deposit.id)}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {deposit.no_akaun || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {deposit.tarikh_resit
                                        ? new Date(deposit.tarikh_resit).toLocaleDateString("en-CA")
                                        : "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {deposit.no_resit || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {deposit.perkara || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {Number(deposit.jumlah || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {Number(deposit.baki || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    ) : (
                      <MDTypography variant="body2" color="text">
                        Tiada rekod deposit dijumpai untuk harta ini.
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 3: // Inbois
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={2} px={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Inbois
                  </MDTypography>
                </MDBox>
                <MDBox p={2}>
                  <MDBox pt={1} pb={2} px={2}>
                    {/* Action buttons */}
                    <Stack direction="row" spacing={2} mb={2}>
                      <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        size="small"
                        onClick={handlePrintInbois}
                        disabled={isInboisLoading || selectedInboisIds.length === 0}
                        sx={buttonStyles.primary}
                      >
                        Cetak Inbois
                      </Button>
                      {(user?.bahagian?.toUpperCase() === 'UEU' || user?.bahagian?.toUpperCase() === 'ADMIN') && (
                        <Button
                          variant="contained"
                          startIcon={<DeleteIcon />}
                          size="small"
                          sx={buttonStyles.delete}
                          onClick={handleDeleteSelectedInbois}
                          disabled={selectedInboisIds.length === 0 || isInboisLoading}
                        >
                          Hapus Inbois ({selectedInboisIds.length})
                        </Button>
                      )}
                    </Stack>

                    {inboisData.length > 0 ? (
                      <MDBox>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ mr: 1 }}>
                          Rekod inbois untuk Kod Harta:{" "}
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold" color="#f42020ff">
                          {hartaData?.kod_harta}
                        </MDTypography>
                        <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
                          <Table size="small">
                            <TableBody>
                              {/* Header row */}
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="caption" fontWeight="bold"></MDTypography>
                                </TableCell>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    indeterminate={
                                      selectedInboisIds.length > 0 &&
                                      selectedInboisIds.length < inboisData.length
                                    }
                                    checked={
                                      inboisData.length > 0 &&
                                      selectedInboisIds.length === inboisData.length
                                    }
                                    onChange={handleSelectAllInbois}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    No Akaun
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Tarikh
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    No Inbois
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Perkara
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Jumlah
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              {/* Data rows */}
                              {inboisData.map((inbois, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {index + 1}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={selectedInboisIds.includes(inbois.id)}
                                      onChange={() => handleSelectInbois(inbois.id)}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {inbois.no_akaun || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {inbois.tarikh_inbois
                                        ? new Date(inbois.tarikh_inbois).toLocaleDateString("en-CA")
                                        : "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {inbois.no_inbois || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {inbois.perkara || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {Number(inbois.jumlah || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </MDTypography>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    ) : (
                      <MDTypography variant="body2" color="text">
                        Tiada rekod inbois dijumpai untuk harta ini.
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      case 4: // Terimaan
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Card sx={{ height: "100%" }}>
                <MDBox pt={2} px={2}>
                  <MDTypography variant="h6" fontWeight="medium">
                    Terimaan
                  </MDTypography>
                </MDBox>
                <MDBox p={2}>
                  <MDBox pt={1} pb={2} px={2}>
                    <MDBox sx={{ pb: 2, display: 'flex', justifyContent: 'flex-start' }}>
                      {(user?.bahagian?.toUpperCase() === 'KEW' || user?.bahagian?.toUpperCase() === 'ADMIN') && (
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<DeleteIcon />}
                          sx={buttonStyles.delete}
                          onClick={handleDeleteSelectedTerimaan}
                          disabled={selectedTerimaanIds.length === 0 || isInboisLoading}
                        >
                          Hapus Terimaan ({selectedTerimaanIds.length})
                        </Button>
                      )}
                    </MDBox>
                    {terimaanData.length > 0 ? (
                      <MDBox>
                        <MDTypography variant="caption" fontWeight="bold" sx={{ mr: 1 }}>
                          Rekod terimaan untuk Kod Harta:{" "}
                        </MDTypography>
                        <MDTypography variant="body2" fontWeight="bold" color="#f42020ff">
                          {hartaData?.kod_harta}
                        </MDTypography>
                        <TableContainer component={Paper} sx={{ ...tableStyles.container, mt: 2 }}>
                          <Table size="small">
                            <TableBody>
                              {/* Header row */}
                              <TableRow>
                                <TableCell>
                                  <MDTypography variant="caption" fontWeight="bold">
                                    {/* Empty for row number */}
                                  </MDTypography>
                                </TableCell>
                                <TableCell padding="checkbox">
                                  <Checkbox
                                    size="small"
                                    indeterminate={selectedTerimaanIds.length > 0 && selectedTerimaanIds.length < terimaanData.length}
                                    checked={terimaanData.length > 0 && selectedTerimaanIds.length === terimaanData.length}
                                    onChange={handleSelectAllTerimaan}
                                  />
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    No Akaun
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Tarikh
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    No Resit
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Perkara
                                  </MDTypography>
                                </TableCell>
                                <TableCell align="center">
                                  <MDTypography variant="caption" fontWeight="bold">
                                    Jumlah
                                  </MDTypography>
                                </TableCell>
                              </TableRow>
                              {/* Data rows */}
                              {terimaanData.map((terimaan, index) => (
                                <TableRow key={index} hover>
                                  <TableCell>
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {index + 1}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell padding="checkbox">
                                    <Checkbox
                                      size="small"
                                      checked={selectedTerimaanIds.includes(terimaan.id)}
                                      onChange={() => handleSelectTerimaan(terimaan.id)}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {terimaan.no_akaun || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {terimaan.tarikh_resit
                                        ? new Date(terimaan.tarikh_resit).toLocaleDateString(
                                            "en-CA"
                                          )
                                        : "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {terimaan.no_resit || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell>
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {terimaan.perkara || "N/A"}
                                    </MDTypography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <MDTypography variant="caption" fontWeight="regular">
                                      {Number(terimaan.jumlah || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </MDTypography>
                                  </TableCell>
                                  {/* Baki cell removed */}
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </MDBox>
                    ) : (
                      <MDTypography variant="body2" color="text">
                        Tiada rekod terimaan dijumpai untuk harta ini.
                      </MDTypography>
                    )}
                  </MDBox>
                </MDBox>
              </Card>
            </Grid>
          </Grid>
        );
      default:
        return null;
    }
  };

  if (authLoading || loading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <MDTypography variant="h4">Loading...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error || !hartaData) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox p={3}>
          <MDTypography variant="h4" color="error">
            {error || "Harta tidak dijumpai"}
          </MDTypography>
          <MDBox mt={2}>
            <MDTypography
              variant="button"
              color="info"
              fontWeight="medium"
              sx={{ cursor: "pointer" }}
              onClick={() => navigate(-1)}
            >
              ← Kembali
            </MDTypography>
          </MDBox>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mb={2} />

      {/* Header with blue box*/}
      <MDBox position="relative" mb={1}>
        <MDBox
          display="flex"
          alignItems="center"
          position="relative"
          minHeight="6rem"
          borderRadius="xl"
          sx={{
            ...pageHeaderStyles.tableHeader,
            backgroundSize: "cover",
            backgroundPosition: "50%",
            overflow: "hidden",
          }}
        />
        <Card
          sx={{
            position: "relative",
            mt: -8,
            mx: 3,
            py: 2,
            px: 2,
          }}
        >
          <Grid container spacing={3} alignItems="center">
            <Grid item>
              <MDBox height="5rem" width="5rem" borderRadius="50%" shadow="xl">
                <MDBox
                  component="div"
                  height="100%"
                  width="100%"
                  borderRadius="50%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  sx={{ backgroundColor: "success.main" }}
                >
                  <Icon fontSize="large" sx={{ color: "white" }}>
                    home
                  </Icon>
                </MDBox>
              </MDBox>
            </Grid>
            <Grid item>
              <MDBox height="100%" mt={0.5} lineHeight={1}>
                <MDTypography variant="h5" fontWeight="medium">
                  {hartaData.nama || hartaData.kod_harta || "N/A"}
                </MDTypography>
                <MDTypography variant="button" color="text" fontWeight="regular">
                  No Akaun: {accountData?.no_akaun || "N/A"}
                </MDTypography>
                {customerContext?.customerName && (
                  <MDTypography variant="caption" color="success" fontWeight="bold" display="block" sx={{ mt: 0.5 }}>
                    Penyewa: {customerContext.customerName}
                  </MDTypography>
                )}
                {userDepartment && (
                  <MDTypography variant="caption" color="info" fontWeight="medium" display="block">
                    Bahagian: {userDepartment}
                  </MDTypography>
                )}
              </MDBox>
            </Grid>
            <Grid item xs={12} md={6} lg={4} sx={{ ml: "auto" }}>
              <MDBox
                display="flex"
                justifyContent={{ md: "flex-end" }}
                alignItems="center"
                lineHeight={1}
                flexDirection={{ xs: "column", sm: "row" }}
                gap={1}
              >
                <MDTypography
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  sx={{ cursor: "pointer", mt: { xs: 1, sm: 0 } }}
                  onClick={() => navigate(-1)}
                >
                  ← Kembali ke Profil Pelanggan
                </MDTypography>
              </MDBox>
            </Grid>
          </Grid>
        </Card>
      </MDBox>

      {/* Tabs Navigation */}
      <MDBox px={3}>
        <Card>
          <MDBox p={2} px={2}>
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              sx={{
                ...tabStyles.filled,
                // Keep responsive flex behavior for 5 tabs
                '& .MuiTab-root': {
                  ...tabStyles.filled['& .MuiTab-root'],
                  flex: { xs: '1 0 33.3333%', md: '1 0 0%' },
                  maxWidth: { xs: 'none', md: 'none' },
                  minWidth: 0,
                  mb: { xs: 1, md: 0 },
                },
              }}
            >
              <Tab label="Maklumat Harta" />
              <Tab label="Lejar" />
              <Tab label="Deposit" />
              <Tab label="Inbois" />
              <Tab label="Terimaan" />
            </Tabs>
          </MDBox>
          {/* Content */}
      <MDBox>
        <MDBox>
          {renderTabContent()}
          </MDBox>
        </MDBox>

      {/* Tambah Deposit Popup */}
      <TambahDeposit
        open={tambahDepositOpen}
        onClose={handleCloseTambahDeposit}
        onSave={handleSaveDeposit}
        noAkaun={getNoAkaun() || ""}
      />
        </Card>
      </MDBox>

    </DashboardLayout>
  );
}

export default HartaDetails;
